import {Box} from "@mui/material";
import {CustomAutoCompleteMenuOptionStyle} from "./Styles";
import CustomTooltip from "./CustomTooltip";
import React from "react";
import {POSITION_MODEL} from "../Helpers/PositionModel";

/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTittle: Full Stack Developer
 *
 * Renders a styled menu option typically used within an autocomplete dropdown list.
 *
 * @param {Object} props - Component properties.
 * @param {string} props.label - The main text label to display.
 * @param {Function} [props.onClick] - Callback function triggered when the option is clicked.
 * @param {Function} [props.subSection] - Optional function that returns additional JSX to display under the label.
 * @param {React.ReactNode} [props.labelExtra] - Optional JSX displayed beside the label, on its same row.
 * @param {React.ElementType} [props.Icon] - Optional icon component to display on the right side.
 * @param {boolean} [props.iconFullHeight] - Draws the left icon in a column of its own, so it covers the label and its subsection instead of being read inside the label.
 *
 *
 */
const CustomAutoCompleteMenuOption= ({label, onClick, subSection, labelExtra = null, Icon, positionIconOptions = POSITION_MODEL.RIGHT, iconFullHeight = false}) => {

    const isIconLeft = !!Icon && positionIconOptions === POSITION_MODEL.LEFT;
    // An icon that names the whole option is read next to every row of it, so it
    // is drawn as a column instead of travelling inside the label.
    const isIconColumn = isIconLeft && iconFullHeight;

    const labelText = (
        <CustomTooltip
            text={label}
            classes={`fs-14px ibl-font-w-600 font-color-general-950 text-ellipsis-1-row p-0 ${isIconColumn ? 'm-0' : ''} ${labelExtra ? 'minw-0' : ''}`}
            showIfTruncated={true}
            icon={isIconLeft && !iconFullHeight && <Icon sx={CustomAutoCompleteMenuOptionStyle.icon} className={"pr-2 w-32px h-32px"}/>}
        />
    );

    return (
        <Box
            sx={isIconColumn ? {...CustomAutoCompleteMenuOptionStyle, paddingLeft: 0, paddingRight: 0} : CustomAutoCompleteMenuOptionStyle}
            onClick={onClick}
        >
            {isIconColumn &&
                <div className={"d-flex flex-shrink-0 align-items-center mr-2"}>
                    <Icon sx={CustomAutoCompleteMenuOptionStyle.icon}/>
                </div>
            }
            <div className={`${isIconColumn ? 'flex-grow-1 minw-0' : isIconLeft ? 'col-12': 'col-11'} p-0`} >
                {/* The row the label is read in is opened next to the icon, so the
                    margins it is laid out with are left out and the title starts
                    where the option does */}
                <div className={`row align-items-center ${isIconColumn ? 'mx-0' : ''} ${labelExtra ? 'flex-nowrap' : ''}`}>
                    {/* Label and labelExtra go in a box of their own, which only takes the width the two need:
                        whatever the label does with the free space of the row stays inside that box, so
                        labelExtra keeps its place beside the label instead of ending at the right of the row //*/}
                    {labelExtra
                        ?
                            <span className={'d-flex align-items-center minw-0'}>
                                {labelText}
                                {labelExtra}
                            </span>
                        :
                            labelText
                    }
                </div>
                {
                    subSection &&
                    <div className={`row ${isIconColumn ? 'mx-0' : ''}`}>
                        {subSection({divider:true})}
                    </div>
                }
            </div>
            {Icon && positionIconOptions === POSITION_MODEL.RIGHT &&  <div className={"col-1 p-0 d-flex justify-content-end"}><Icon sx={CustomAutoCompleteMenuOptionStyle.icon}/></div>}
        </Box>
    );
};

export default CustomAutoCompleteMenuOption
